/**
 * **Os sorteios da sala, e porque eles não são aleatórios** (gate V28H.W).
 *
 * Três cartas passaram a ter um "entre X e Y" na descrição aprovada - a duração
 * do Congelamento, a hipótese de ele também travar o ataque, e a duração da
 * Lentidão - e isso obriga a uma decisão que este projecto nunca tinha tido de
 * tomar num duelo: **de onde vem o acaso**.
 *
 * ## Porque não pode ser `Math.random`
 *
 * Porque a sala **re-simula**. O servidor repete a janela a partir da
 * instantânea para verificar o que o cliente afirmou, e uma tirada que desse
 * outro resultado na repetição faria a verificação recusar uma partida honesta -
 * ou, pior, aceitar uma desonesta. Um sorteio numa sala autoritativa tem de ser
 * uma FUNÇÃO do estado que já viaja.
 *
 * ## O que entra, e porque isso chega
 *
 * O id do projéctil e um número que distingue a pergunta. O id é atribuído pela
 * sala por um contador que a instantânea guarda, portanto ele é o mesmo na
 * primeira passagem e na repetição - e é diferente para cada tiro, que é o que
 * faz duas esferas seguidas não darem sempre a mesma coisa.
 *
 * O `salt` separa perguntas sobre o MESMO projéctil: a duração e o travamento
 * do ataque são duas tiradas independentes, e sem ele seriam a mesma resposta
 * lida duas vezes.
 *
 * ## Aritmética inteira, e só ela
 *
 * `Math.random` não é o único perigo: uma potência ou uma raiz também dariam
 * resultados diferentes em máquinas diferentes. Isto é `Math.imul`, XOR e
 * deslocamento sem sinal - aritmética de 32 bits EXACTA, definida ao bit pela
 * norma da linguagem e igual em qualquer motor.
 *
 * ## O erro que isto teve, e que o obriga a ser assim
 *
 * A primeira versão multiplicava com `*` e reduzia com `% 4 294 967 296`, e
 * misturava isso com `^` e `>>>`. Os dois últimos convertem para inteiro de 32
 * bits **com sinal**, portanto `x ^ (x >>> 13)` podia sair NEGATIVO - e o resto
 * de um número negativo é negativo em JavaScript. O resultado: uma duração de
 * `2 000 + (-2) * 1 000 = 0`, `applyCardTimer` a recusar um temporizador de zero,
 * e o Congelamento a acertar no adversário sem lhe fazer absolutamente nada.
 *
 * Medido, e não deduzido: o dardo chegava ao casco, o ramo do acerto corria, e a
 * ranhura ficava a zero. É por isso que o mínimo, o máximo e o sinal desta
 * função têm prova própria em `rolls.test.ts` - um sorteio que sai do intervalo
 * não parte nada de maneira visível, só faz uma carta não funcionar.
 */

/** O denominador de tudo o que este ficheiro devolve. */
const BPS = 10_000;

/**
 * Um número entre 0 e 9 999, decidido pelo id e pela pergunta.
 *
 * O hash é o mesmo que a arte usa para espalhar partículas, e é uma escolha
 * deliberada: um gerador com boa dispersão nos bits baixos, porque é deles que
 * o resto sai.
 */
export function pvpRollBps(projectileId: number, salt: number): number {
  /*
   * `Math.imul` e `>>> 0` em cada passo, e nenhum `*` solto: o primeiro dá o
   * produto de 32 bits exacto e o segundo devolve o valor ao domínio sem sinal
   * antes do passo seguinte. É o que impede um valor negativo de chegar ao
   * resto lá em baixo.
   */
  let x = (Math.imul(projectileId >>> 0, 2_654_435_761) + Math.imul(salt >>> 0, 40_503)) >>> 0;
  x = Math.imul(x ^ (x >>> 15), 2_246_822_519) >>> 0;
  x = Math.imul(x ^ (x >>> 13), 3_266_489_917) >>> 0;
  x = (x ^ (x >>> 16)) >>> 0;
  return x % BPS;
}

/**
 * Uma duração inteira em segundos, entre um mínimo e um número de degraus.
 *
 * Degraus e não um contínuo, e é uma decisão de APRESENTAÇÃO tanto quanto de
 * regra: o Congelamento tem três camadas de gelo desenhadas - leve, média,
 * intensa - e elas só podem dizer a verdade se a duração cair sempre num dos
 * três valores. Um sorteio contínuo entre 2 000 e 4 000 ms daria 2 837 ms, e
 * nenhuma das três camadas seria honesta.
 */
export function pvpRollDurationMs(
  projectileId: number,
  salt: number,
  minimumMs: number,
  steps: number,
): number {
  const step = pvpRollBps(projectileId, salt) % Math.max(1, steps);
  return minimumMs + step * 1_000;
}

/** Esta tirada passou? `chanceBps` de 3 000 é trinta por cento das vezes. */
export function pvpRollHits(projectileId: number, salt: number, chanceBps: number): boolean {
  return pvpRollBps(projectileId, salt) < chanceBps;
}
